import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import type { StepperStep } from '../../models/stepper/stepper-step';
import { isDefined } from '../../utils';

@Injectable({
    providedIn: 'root',
})
export class StepperService<T> {
    backButton$ = new BehaviorSubject<boolean>(null);

    currentStepIndex = 0;
    highlightedStepIndex = 0;
    isPendingActionFooterState: boolean;

    private _steps: StepperStep<T>[] = [];
    private _hideSteps: boolean;

    get steps() {
        return this._steps;
    }

    get hasPreviousStep() {
        return this.currentStepIndex > 0;
    }

    get isLastStep() {
        return this.currentStepIndex + 1 >= this._steps.length;
    }

    get hasNextButton() {
        return this._steps.length > this.currentStepIndex
            ? !this._steps[this.currentStepIndex].hideNextButton
            : false;
    }

    get hideSteps() {
        return this._hideSteps;
    }

    get currentStep(): StepperStep<T> {
        return this.getStep(this.currentStepIndex);
    }

    /**
     * @param steps
     * @param hideSteps - keep stepper component instantiated but hide the actual steps. Needed for cases
     * when we want to have an empty stepper navbar rendered
     */
    initSteps(steps: StepperStep<T>[], hideSteps = false) {
        this._steps = [...steps];
        this._hideSteps = hideSteps;
    }

    getStep(index: number): StepperStep<T> {
        return this._steps[index];
    }

    getNextStep(): StepperStep<T> {
        return this.getStep(this.currentStepIndex + 1);
    }

    getPrevStep(): StepperStep<T> {
        return this.getStep(this.currentStepIndex - 1);
    }

    isCurrentlyActiveStep(step: T): boolean {
        return this.getStep(this.currentStepIndex).type === step;
    }

    reset() {
        this._steps = [];
        this.currentStepIndex = 0;
        this.highlightedStepIndex = 0;
        this.isPendingActionFooterState = false;
    }

    computeCurrentStepIndex(stepType: T) {
        const currentStepIndex = this._steps.findIndex(
            (step) => step.type === stepType,
        );
        this.currentStepIndex = currentStepIndex > -1 ? currentStepIndex : 0;
        const currentStep = this.getStep(this.currentStepIndex);
        this.highlightedStepIndex = isDefined(currentStep.parentType)
            ? this._steps.findIndex(
                  (step) => step.type === currentStep.parentType,
              )
            : this.currentStepIndex;
    }

    togglePendingActionFooterState(isPendingActionState: boolean) {
        this.isPendingActionFooterState = isPendingActionState;
    }
}
